import React from "react"
import { Tag, Image, Dropdown, Avatar } from "antd"
import { EditOutlined, DeleteOutlined, StopOutlined, CheckCircleOutlined, UserOutlined } from "@ant-design/icons"
import { Link } from "react-router-dom"
import styles from "./CrewMembersTable.module.css"
import { formatPhoneNumber } from "utils/data-formatters"
import { routes } from "router/routes"
import PropTypes from "prop-types"

const CrewMemberNameCell = ({ text, record }) => {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {record.image ? (
        <Image src={record.image} width={40} height={40} style={{ objectFit: "cover", borderRadius: 300 }} preview={false} />
      ) : (
        <Avatar icon={<UserOutlined />} size={40} />
      )}
      <Link to={routes.guideCrewMemberDetailsPath({ id: record.id })} className={styles.nameLink}>
        {text}
      </Link>
    </div>
  )
}

CrewMemberNameCell.propTypes = {
  text: PropTypes.string,
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    image: PropTypes.string
  }).isRequired
}

export const crewMembersColumns = (onEdit, onDelete, onDeactivate, onActivate, onAssignClick, currentGuideProfileId = null) => [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    align: "center",
    sorter: true,
    className: styles.noWrapCell,
    render: (text, record) => <CrewMemberNameCell text={text} record={record} />
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    className: styles.noWrapCell
  },
  {
    title: "Role",
    dataIndex: "role",
    key: "role",
    sorter: true,
    className: styles.noWrapCell,
    render: (role) => {
      const roleName = typeof role === "object" ? role?.name : role
      return <Tag className={styles.primaryTag}>{roleName || "-"}</Tag>
    }
  },
  {
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    className: styles.noWrapCell,
    render: (phone) => formatPhoneNumber(phone)
  },
  {
    title: "Assigned to",
    dataIndex: "assignedTo",
    key: "assignedTo",
    sorter: true,
    className: styles.noWrapCell,
    render: (assignedTo, record) => {
      if (typeof assignedTo === "number") {
        const content = `${assignedTo} ${assignedTo === 1 ? "experience" : "experiences"}`

        if (onAssignClick && record?.can_edit) {
          return (
            <button
              onClick={() => onAssignClick(record)}
              className={styles.assignedExperiencesButton}
              type="button"
            >
              {content}
            </button>
          )
        }

        return <Tag className={styles.defaultTag}>{content}</Tag>
      }
      return <Tag className={styles.primaryTag}>{assignedTo}</Tag>
    }
  },
  {
    title: "Upcoming",
    dataIndex: "upcomingBookings",
    key: "upcomingBookings",
    sorter: true,
    className: styles.noWrapCell
  },
  {
    title: "Availability",
    dataIndex: "availability",
    key: "availability",
    sorter: true,
    className: styles.noWrapCell
  },
  {
    title: "Next Time Off",
    dataIndex: "nextTimeOff",
    key: "nextTimeOff",
    sorter: true,
    className: styles.noWrapCell
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: true,
    className: styles.noWrapCell,
    render: (status) => {
      const normalized = (status || "").toLowerCase()
      let color = "default"
      if (normalized === "active") color = "green"
      else if (normalized === "deactivated" || normalized === "inactive") color = "red"
      const displayStatus = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "—"

      return <Tag color={color}>{displayStatus}</Tag>
    }
  },
  {
    title: "Actions",
    key: "actions",
    align: "right",
    render: (_, record) => {
      const isDeactivated = record?.status?.toLowerCase() === "deactivated"
      const isSelf = currentGuideProfileId && String(record?.id) === String(currentGuideProfileId)
      const roleName = typeof record?.role === "object" ? record?.role?.name : record?.role
      const isBusinessOwner = roleName?.toLowerCase() === "business owner"

      // Staff cannot perform any action against a Business Owner (unless it's their own row)
      if (!record?.can_edit || (isBusinessOwner && !isSelf)) {
        return null
      }

      const menuItems = [
        {
          key: "edit",
          icon: <EditOutlined />,
          label: "Edit"
        },
        !isSelf && {
          key: isDeactivated ? "activate" : "deactivate",
          icon: isDeactivated ? <CheckCircleOutlined /> : <StopOutlined />,
          label: isDeactivated ? "Make Active" : "Deactivate"
        },
        !isSelf && {
          key: "delete",
          icon: <DeleteOutlined className={styles.deleteIcon} />,
          label: "Delete"
        }
      ].filter(Boolean)

      return (
        <Dropdown
          trigger={["click"]}
          menu={{
            className: styles.actionMenu,
            items: menuItems,
            onClick: ({ key }) => {
              if (key === "edit" && onEdit) {
                onEdit(record)
              } else if (key === "deactivate" && onDeactivate) {
                onDeactivate(record)
              } else if (key === "activate" && onActivate) {
                onActivate(record)
              } else if (key === "delete" && onDelete) {
                onDelete(record)
              }
            }
          }}
          placement="bottomRight"
          arrow
        >
          <span className={styles.actionTrigger}>⋯</span>
        </Dropdown>
      )
    }
  }
]

export default crewMembersColumns
